<template>
  <section class="hero is-default is-bold">
    <div class="hero-body">
      <div class="container">
        <div class="columns">
          <div class="column is-8 is-offset-2">
            <unvalid-link v-if="unvalidLink" />
            <obsolete-link
              v-else-if="!surveyActive"
              :entityName="entityName"
              :contactEmail="contactEmail"
            />
            <template v-else>
              <preamble
                v-show="activeStep === 0"
                :surveyMessage="surveyMessage"
                @submit="nextStep"
              />
              <survey-form
                v-show="activeStep === 1"
                v-model="survey"
                :isSurvey="true"
                :year="year"
                :totalStep="totalStep"
                :surveyTagCategories="surveyTagCategories"
                @submit="nextStep"
              />
              <commute-form
                v-show="activeStep === 2"
                v-model="survey"
                :isSurvey="true"
                :startStep="startStep"
                :totalStep="totalStep"
                :year="year"
                :hasWorkingDay2="hasWorkingDay2"
                :isSimulation="false"
                @updateHasWorkingDay2="updateHasWorkingDay2"
                @submit="nextStep"
                @back="backStep"
              />
              <ok-to-answer-food-form
                v-show="activeStep === 3"
                v-model="okToAnswerFoodForm"
                :startStep="startStepFood"
                :totalStep="totalStep"
                @submit="nextStep"
                @back="backStep"
              />
              <food-form
                v-show="activeStep === 4"
                v-model="survey"
                :isSurvey="true"
                :startStep="startStepFood + 1"
                :totalStep="totalStep"
                :year="year"
                :isSimulation="false"
                @submit="nextStep"
                @back="backStep"
              />
              <confirmation-form
                v-show="activeStep === 5"
                v-model="survey"
                :year="year"
                :commutesActive="commutesActive"
                :foodsActive="foodsActive"
                @edit="backStep"
                @submit="nextStep"
              />
              <result
                v-show="activeStep === 6"
                :survey="survey"
                :year="year"
                :commutesActive="commutesActive"
                :foodsActive="foodsActive"
                :isSurvey="true"
                :simulations="[simulation]"
                :citySize="citySize"
              />
            </template>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import carbonService from '@/services/carbonService'
import Commute from '@/models/carbon/Commute.js'
import Food from '@/models/carbon/Food.js'
import Survey from '@/models/carbon/Survey.js'
import TagCategory from '@/models/core/TagCategory.js'
import { useCoreStore } from '@/stores/core.js'

export default {
  name: 'Survey',
  components: {
    'unvalid-link': () =>
      import('@/components/carbon/forms/survey/UnvalidLink.vue'),
    'obsolete-link': () =>
      import('@/components/carbon/forms/survey/ObsoleteLink.vue'),
    preamble: () => import('@/components/carbon/forms/survey/Preamble.vue'),
    'survey-form': () => import('@/components/carbon/forms/survey/Form.vue'),
    'confirmation-form': () =>
      import('@/components/carbon/forms/survey/ConfirmationForm.vue'),
    'ok-to-answer-food-form': () =>
      import('@/components/carbon/forms/survey/OkToAnswerFoodForm.vue'),
    result: () => import('@/components/carbon/forms/survey/Result.vue'),
    'commute-form': () => import('@/components/carbon/forms/commutes/Form.vue'),
    'food-form': () => import('@/components/carbon/forms/foods/Form.vue')
  },
  setup () {
    const coreStore = useCoreStore()
    return { coreStore }
  },
  data () {
    return {
      survey: Survey.createEmpty(),
      year: new Date().getFullYear(),
      surveyMessage: null,
      commutesActive: false,
      foodsActive: false,
      surveyTagCategories: [],
      contactEmail: null,
      entityName: null,
      citySize: 0,
      unvalidLink: false,
      error: '',
      activeStep: 0,
      hasWorkingDay2: false,
      okToAnswerFoodForm: true
    }
  },
  computed: {
    surveyActive: function () {
      return this.commutesActive || this.foodsActive
    },
    uuid: function () {
      return this.$route.params.uuid
    },
    simulation: function () {
      return {
        commute: Commute.createFromObj(this.survey),
        food: Food.createFromObj(this.survey),
        year: this.year
      }
    },
    positions () {
      return this.coreStore.surveyPositionTitles.map((x) => x.name)
    },
    startStep () {
      if (this.positions.length === 1) {
        return 2
      } else {
        return 3
      }
    },
    startStepFood: function () {
      if (this.commutesActive) {
        if (this.survey.nWorkingDay === 1) {
          return this.startStep + 2
        } else if (this.hasWorkingDay2) {
          return this.startStep + 6
        } else {
          return this.startStep + 3
        }
      } else {
        // the question to answer the food survey is not asked
        return this.startStep - 1
      }
    },
    totalStep: function () {
      let totalStep = 2
      if (this.survey.nWorkingDay === null) {
        totalStep = 8
      } else if (this.survey.nWorkingDay === 0) {
        totalStep = 1
      } else if (this.survey.nWorkingDay > 0) {
        if (this.commutesActive) {
          if (this.survey.nWorkingDay === 1) {
            totalStep += 2
          } else if (this.hasWorkingDay2) {
            totalStep += 6
          } else {
            totalStep += 3
          }
        }
        if (this.foodsActive) {
          if (this.okToAnswerFoodForm) {
            totalStep += 3
          } else {
            totalStep += 1
          }
        }
        if (this.foodsActive && !this.commutesActive) {
          totalStep -= 1
        }
        if (this.positions.length === 1) {
          totalStep -= 1
        }
        // Add the tags question
        if (this.surveyTagCategories.length > 0) {
          totalStep += 1
        }
      }
      return totalStep
    }
  },
  methods: {
    updateHasWorkingDay2 (value) {
      this.hasWorkingDay2 = value
    },
    nextStep: function () {
      if (this.activeStep === 1 && this.survey.nWorkingDay === 0) {
        this.activeStep = 5
      } else if (this.activeStep === 1 && !this.commutesActive) {
        this.activeStep = 4
      } else if (this.activeStep === 2 && !this.foodsActive) {
        this.activeStep = 5
      } else if (this.activeStep === 3 && !this.okToAnswerFoodForm) {
        this.activeStep = 5
      } else if (this.activeStep === 5) {
        this.submit()
        this.activeStep = 6
      } else {
        this.activeStep += 1
      }
    },
    backStep: function () {
      if (this.activeStep === 5) {
        if (this.survey.nWorkingDay === 0) {
          this.activeStep = 1
        } else if (!this.foodsActive) {
          this.activeStep = 2
        } else if (!this.okToAnswerFoodForm) {
          this.activeStep = 3
        } else {
          this.activeStep -= 1
        }
      } else if (this.activeStep === 4 || this.activeStep === 3) {
        if (!this.commutesActive) {
          this.activeStep = 1
        } else {
          this.activeStep -= 1
        }
      } else {
        this.activeStep -= 1
      }
    },
    submit () {
      this.survey = Survey.createFromObj(this.survey)
      this.survey.clean()
      carbonService
        .saveSurvey({
          uuid: this.uuid,
          survey: this.survey.toDatabase()
        })
        .then((data) => {
          this.survey = Survey.createFromObj(data)
          window.scrollTo(0, 0)
        })
        .catch((error) => {
          this.error = error.data
        })
    }
  },
  mounted () {
    const loadingComponent = this.$buefy.loading.open({
      container: null
    })
    carbonService
      .getGHGISurveyInfo({
        uuid: this.uuid
      })
      .then((data) => {
        if (data) {
          this.year = data.year
          this.surveyMessage = data.surveyMessage
          this.contactEmail = data.contactEmail
          this.entityName = data.entityName
          this.commutesActive = data.commutesActive
          this.foodsActive = data.foodsActive
          this.surveyTagCategories = data.surveyTagCategories.map((obj) =>
            TagCategory.createFromObj(obj)
          )
          this.citySize = data.citySize
          this.$route.meta.year = this.year
          this.$route.meta.subtitle = this.entityName
          // Add a temporary query parameter
          this.$router.replace({ query: { temp: Date.now() } })
          // Remove the temporary query parameter
          this.$router.replace({ query: { temp: undefined } })
        } else {
          this.unvalidLink = true
        }
        loadingComponent.close()
      })
      .catch((error) => {
        this.error = error.response.data
        loadingComponent.close()
      })
  }
}
</script>
